import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import React, { useState } from 'react';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import getStatusBarColor from '../../utils/getStatusBarColor';
import ConsoleCard from '../MainPageLayout/ConsoleCard';
import MenuBoardTimeTile from './MenuBoardTimeTile';
import ResponseTimeTile from './ResponseTimeTile';
import TurnTile from './TurnTile';
import SentimentTile from './SentimentTile';
import PulseSignals from './PulseSignals';
import PulseAdvise from './PulseAdvise';
import { useAppSelector } from '../../app/hooks';
import {
  selectAIStatus,
  selectIsAIEscalation,
  selectAIEscalationReason,
  selectRequiresTakeover,
  selectIsAIEscalatedOnCarEnter,
  selectIsAIAutoMode,
} from '../../redux/features/ai/ai.selector';
import {
  AIStatusColors,
  DEFAULT_STATUS_MESSAGE,
} from '../../redux/features/ai/ai.constants';
import { selectFeatureFlagUnresolvedItems } from '../../redux/features/config/config.selector';
import { selectIsCarPresent } from '../../selectors/message';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';

/**
 * CUSTOMER PULSE (left pane, top card).
 *
 * A collapsible card that gives the human a 2-second read of the live order's AI
 * status. The header (icon + title + collapse chevron) sits inside the white card
 * (matching Action Center); a full-bleed status band pinned at the card's bottom
 * edge encodes the LIVE AI status — green/yellow/red soft tints, plus a prominent
 * vivid blink on takeover.
 *
 * Under the header the card is a compact strip: the Menu Board Time + Response
 * time meters, the live signal + reminder chips (PulseSignals), and the folded-in
 * AI co-pilot ask (PulseAdvise) — with the AI status band pinned at the bottom.
 */

const useStyles = makeStyles((theme: Theme) => ({
  // AI status band (folded-in AIStatusBar): a full-bleed strip pinned at the
  // card's bottom edge (marginTop:auto pushes it down in the flex column).
  // Background encodes the LIVE AI status and is applied inline via the shared
  // getStatusBarColor() so it matches the legacy AIStatusBar exactly (vivid
  // green/yellow/red), plus a prominent vivid blink for a takeover
  // (bandTakeover). The card padding is 0, so the band already spans edge-to-edge
  // (no negative margins needed); the shared 8px inset pads its text on all sides.
  statusBand: {
    flexShrink: 0,
    marginTop: 'auto',
    padding: HITL3_PADDING,
    // Text styled like the "Continue" intent pill: medium weight, shared hitl3 size.
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
    lineHeight: 1.3,
    // Left-aligned text, vertically centered within the band's available height.
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  // One line: truncate with an ellipsis (no wrap, no h-scroll). minWidth:0 lets
  // this flex child shrink so the ellipsis engages; the full message shows on
  // hover via the title attribute on the band.
  statusText: {
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Takeover: a prominent vivid red throb (light↔dark) with white text — a
  // "must not miss" alert, so intentionally NOT a soft tint. Static $keyframes
  // reference (JSS resolves the scoped name only in a static rule).
  '@keyframes statusTakeoverPulse': {
    '0%, 100%': { backgroundColor: '#ff5c5c' },
    '50%': { backgroundColor: '#990000' },
  },
  bandTakeover: {
    color: Colors.white,
    animation: '$statusTakeoverPulse 1s ease-in-out infinite',
  },
  // HITL 3.0 metric tiles row, laid out side-by-side under the header. Each tile
  // self-gates to null, so the row can be empty until one becomes active.
  // flex-start (not center) tops-align the tiles so every tile's value + label
  // sit on the same line — tiles with a progress bar (Menu Board Time, Response
  // time) are taller than those without (Turn, Sentiment), and centering would
  // otherwise push the shorter tiles' text down.
  metricRow: {
    marginTop: HITL3_PADDING,
    paddingLeft: HITL3_PADDING,
    paddingRight: HITL3_PADDING,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

interface CustomerPulseProps {
  // Optional controlled collapse so the parent can drive the resizable panel
  // (collapsing frees its space for the transcript). Falls back to internal
  // state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CustomerPulse = ({
  open: openProp,
  onOpenChange,
}: CustomerPulseProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  // AI status band — wired to the live AI status. Precedence:
  // takeover-blink > agent-intercepted(red) > escalation(red / car-enter orange)
  // > AI status color (green/yellow/red).
  const aiStatus = useAppSelector(selectAIStatus);
  const isAIEscalation = useAppSelector(selectIsAIEscalation);
  const aiEscalationReason = useAppSelector(selectAIEscalationReason);
  const requiresTakeover = useAppSelector(selectRequiresTakeover);
  const isAIEscalatedOnCarEnter = useAppSelector(selectIsAIEscalatedOnCarEnter);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const unresolvedItemsEnabled = useAppSelector(
    selectFeatureFlagUnresolvedItems
  );
  const isCarPresent = useAppSelector(selectIsCarPresent);
  const { isStaffInterventionHappening } = useIntervention();

  // Between cars the control state still carries the departed car's intercept/
  // escalation values: Finish Order deliberately skips the control reset while
  // an intercepted car is present (see OrderActions), and the state only
  // re-arms on the next session start / task pickup. That staleness is masked
  // HERE, at the display layer, by gating on isCarPresent — the state itself
  // must NOT be reset on car exit. Forcing isAIAutoMode back to auto while the
  // departed car's order is still open makes the post-car-exit auto-submit
  // silently drop the order (sendOrder bails in auto mode — orderSlice), lets
  // a lingering hypothesis frame rebuild the cart over the agent's edits, and
  // re-arms the take-over confirm prompt with no car to take over.
  //
  // A takeover was requested (the AI halted). While the AI is still nominally in
  // control (isAIAutoMode) the band BLINKS — a "take over now" alert. The moment
  // the agent takes any manual action, isAIAutoMode flips to false: the takeover
  // has been intercepted, so the blink resolves to a static "Agent interception"
  // in the vivid escalation red. requiresTakeover is sticky, so this holds
  // until the session resets for the next order — the car-presence gate is what
  // keeps it off the band once the car has left.
  const takeoverActive =
    unresolvedItemsEnabled && requiresTakeover && isCarPresent;
  const showPulse = takeoverActive && isAIAutoMode;
  const agentIntercepted = takeoverActive && !isAIAutoMode;
  const showEscalation = isAIEscalation && isCarPresent;
  // With no car at the speaker the band idles on the default green — the live
  // aiStatus/escalation describe a conversation that is over.
  const displayedAIStatus = isCarPresent
    ? aiStatus
    : { status: AIStatusColors.green, message: DEFAULT_STATUS_MESSAGE };

  // Staff intervention outranks every AI status: a human now owns the
  // conversation, so the band reads "Staff Intervention" (static, no blink)
  // regardless of the underlying AI/takeover state.
  const statusMessage = isStaffInterventionHappening
    ? 'Staff Intervention'
    : agentIntercepted
    ? 'Agent Intercept'
    : showEscalation
    ? aiEscalationReason || displayedAIStatus.message
    : displayedAIStatus.message;

  // The takeover blink is suppressed during staff intervention — the static
  // "hand-off" red is the resolved, unambiguous state.
  const showTakeoverBlink = showPulse && !isStaffInterventionHappening;

  // Background/text match the legacy AIStatusBar exactly: showPulse drives the
  // vivid takeover blink via the bandTakeover class; every other state derives
  // its background from the shared getStatusBarColor(). agentIntercepted is a
  // HITL 3.0-only static state (the legacy bar unmounts on intercept) — render
  // it in the same vivid escalation red. Text is white on the saturated
  // escalation/intercept fills (as in AIStatusBar) and inherit (dark) otherwise.
  const bandBackground = isStaffInterventionHappening
    ? Colors.torchRed
    : agentIntercepted
    ? Colors.red
    : getStatusBarColor(
        showEscalation,
        isAIEscalatedOnCarEnter,
        displayedAIStatus.status
      );
  const bandTextColor =
    isStaffInterventionHappening || showEscalation || agentIntercepted
      ? Colors.white
      : 'inherit';

  return (
    <ConsoleCard
      icon={<AutoAwesomeOutlinedIcon />}
      title="AI Copilot"
      open={open}
      onToggle={toggleOpen}
      testId="customer-pulse"
      footer={
        // AI status band ALWAYS renders — the live status (and the takeover
        // blink) must stay visible even when the card is collapsed to a strip.
        // marginTop:auto pins it to the card's bottom edge in both states.
        <div
          className={`${classes.statusBand}${
            showTakeoverBlink ? ` ${classes.bandTakeover}` : ''
          }`}
          style={
            showTakeoverBlink
              ? undefined
              : { backgroundColor: bandBackground, color: bandTextColor }
          }
          title={statusMessage}
        >
          <span className={classes.statusText}>{statusMessage}</span>
        </div>
      }
    >
      {/* HITL 3.0 Pulse strip: the metric tiles (side by side) → the signal +
          reminder chips → the folded-in AI ask. The whole console is already
          gated on hitl_3_0 at the mount site (Body), so no local flag branch is
          needed. Each tile self-gates:
            • Menu Board Time — while a session is active; the car's dwell gauge.
            • Turn — active session + AI enabled; the ai_session_state turn count
              (no progress bar).
            • Sentiment — active session + AI enabled; the ai_session_state
              sentiment (colour + face, muted "—" default).
            • Response time — active session + (agent intercepted OR pure HITL);
              the per-turn reply SLA.
          PulseSignals / PulseAdvise self-gate to null when there's nothing to
          surface.

          During staff intervention the whole body is blanked — the metrics,
          signals and AI ask all describe an AI-run order that no longer applies
          once a human takes over. Only the AI status band (footer) stays, now
          reading "Staff Intervention". */}
      {!isStaffInterventionHappening && (
        <>
          <div className={classes.metricRow}>
            <MenuBoardTimeTile />
            <TurnTile />
            <SentimentTile />
            <ResponseTimeTile />
          </div>
          <PulseSignals />
          <PulseAdvise />
        </>
      )}
    </ConsoleCard>
  );
};

export default CustomerPulse;
